import {
  abbreviateId,
  Box,
  type CustomerQuote,
  formattedDate,
  FormControlSizes,
  HStack,
  IconButton,
  IconName,
  ModeEnum,
  prettyName,
  QuoteSide,
  QuoteStatus,
  readableDate,
  SecurityOrSecuritiesRenderer,
  Text,
  Tooltip,
  useJsonModal,
  VStack,
} from '@talos/kyoko';
import { compact } from 'lodash-es';
import { Fragment, type ReactNode, useMemo } from 'react';
import { useCustomersByName } from '../../../../hooks/useCustomer';
import { NegotiationStatus, NegotiationStatusLabel } from '../../../SummaryCard/NegotiationStatus';
import { FixingDetailsIndicesLabel } from '../../NewRFQ/Components/FixingDetailsIndicesLabel';
import { PricingPanelSettingsButton } from './PricingPanelSettingsButton';

interface PricingPanelHeaderProps {
  quote: CustomerQuote;
}

export const PricingPanelHeader = ({ quote }: PricingPanelHeaderProps) => {
  const customersByName = useCustomersByName();

  /** Combines a few properties, null-checks, and returns a concatenated string for display all together */
  const leftSideSubDetails: ReactNode[] = useMemo(() => {
    return [
      formattedDate(quote.SubmitTime),
      prettyName(quote.PricingMode),
      <FixingDetailsIndicesLabel key="fixing-details" fixingDetails={quote.FixingDetails} />,
    ];
  }, [quote]);

  const rightSideDetails: DetailParams[] = useMemo(() => {
    const cpty = customersByName?.get(quote.Counterparty);
    return compact([
      {
        header: <NegotiationStatusLabel />,
        value: <NegotiationStatus entity={quote} />,
      },
      {
        header: 'ID',
        value: abbreviateId(quote.RFQID),
        valueTooltip: quote.RFQID,
        testID: 'manual-pricing-panel-rfqid',
      },
      quote.SettleDate && { header: 'Settlement', value: readableDate(quote.SettleDate) },
      quote.NegotiationParameters?.SizeCountering === ModeEnum.Enabled && {
        header: 'Size Countering',
        value: 'Enabled',
      },
      quote.NegotiationParameters?.PartialFill === ModeEnum.Enabled && {
        header: 'Partial Fills',
        value: 'Enabled',
      },
      { header: 'Customer', value: cpty?.DisplayName ?? quote.Counterparty },
      { header: 'Quantity', value: quote.OrderQty }, // fixup for later
      {
        header: 'Status',
        value: <QuoteStatus quoteStatus={quote.QuoteStatus} rfqProtocol={quote.RFQProtocol} />,
      },
    ]);
  }, [quote, customersByName]);

  const { handleClickJson, jsonModal } = useJsonModal();

  return (
    <>
      <HStack justifyContent="space-between" gap="spacing8" p="spacing8" background="backgroundContent">
        {/* Left part */}
        <VStack gap="spacing2" alignItems="flex-start">
          <HStack gap="spacing4" fontWeight="500">
            <Text>RFQ - SPOT</Text>
            <SecurityOrSecuritiesRenderer symbolOrSymbols={quote.symbolOrSymbols} />
            <QuoteSide side={quote.Side} />
            <IconButton size={FormControlSizes.Xxs} dim icon={IconName.Braces} onClick={() => handleClickJson(quote)} />
          </HStack>

          <HStack gap="spacing4" fontSize="fontSizeSm">
            {leftSideSubDetails.map((details, i) => (
              <Fragment key={i}>
                <Text>{details}</Text>
                {leftSideSubDetails.length - 1 !== i && <Text>-</Text>}
              </Fragment>
            ))}
          </HStack>
        </VStack>

        {/* Right part */}
        <HStack gap="spacing8">
          {rightSideDetails.map((detail, i) => (
            <Detail key={i} {...detail} />
          ))}
          <PricingPanelSettingsButton />
        </HStack>
      </HStack>
      {jsonModal}
    </>
  );
};

interface DetailParams {
  header: ReactNode;
  value: ReactNode;
  valueTooltip?: string;
  testID?: string;
}

const Detail = ({ header, value, valueTooltip, testID }: DetailParams) => {
  return (
    <VStack gap="spacing2" alignItems="flex-start" data-testid={testID}>
      <Box textTransform="uppercase" fontSize="fontSizeSm">
        {header}
      </Box>
      <Tooltip tooltip={valueTooltip}>
        <Box color="colorTextImportant" fontWeight="500">
          {value}
        </Box>
      </Tooltip>
    </VStack>
  );
};
